/**
 * 我的买卖 · 出售信息 / 购买信息详情 (ys142857 parity).
 */
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { otcAPI, type OtcMyTradeRow } from '../api/otc';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { ThemedAvatar } from '../components/ui/ThemedSurface';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { QueryErrorNote } from '../components/ui/QueryErrorNote';
import { OTC_COLORS as C } from '@/constants';
import { tokenDisplayLabel } from '@/utils/orb-display';

function fmtNum(v: string | undefined, digits = 0): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function fmtLegacyTime(value: string): string {
  const s = value.trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s;
  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return s.replace('T', ' ').slice(0, 19);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
}

export function OtcMyTradeDetail() {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const side = searchParams.get('side') === 'buy' ? 'buy' : 'sell';
  const isSell = side === 'sell';
  const title = isSell ? '出售信息' : '购买信息';
  const qtyLabel = isSell ? '出售数量' : '购买数量';
  const counterpartyLabel = isSell ? '买家' : '卖家';
  const listTitle = isSell ? '买家列表' : '卖家列表';
  const emptyListHint = isSell ? '暂无买家记录' : '暂无卖家记录';

  const location = useLocation();
  const preview = (location.state as { row?: OtcMyTradeRow } | null)?.row;

  const detailQuery = useQuery({
    queryKey: ['otc', 'hall', 'detail', id, side],
    queryFn: () => otcAPI.getHallDetail(id),
    enabled: Boolean(id),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const showSkeleton = detailQuery.isPending && !detailQuery.data && !preview;

  const headerTheme = {
    topbarBg: C.topbarBg,
    titleGradient: C.titleGradient,
    text: C.text,
  };

  const detail = detailQuery.data;
  const listTime = fmtLegacyTime(
    detail?.create_time ?? preview?.create_time ?? '',
  );
  const soldQty = detail?.listed_uo ?? preview?.amount_uo ?? detail?.traded_uo ?? '0';
  const showHeader = Boolean(detail || preview);
  const showBuyerSkeleton = detailQuery.isPending && !detail;

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={title} onBack={() => navigate(-1)} theme={headerTheme} />

        {showSkeleton ? (
          <div className="space-y-3 px-4 pt-[68px]">
            <LoadingSkeleton className="h-28" />
            <LoadingSkeleton className="h-52" />
          </div>
        ) : detailQuery.isError || !showHeader ? (
          <div className="px-4 pt-[68px]">
            <QueryErrorNote message="加载失败" onRetry={() => void detailQuery.refetch()} />
          </div>
        ) : (
          <div className="space-y-4 px-4 pb-8 pt-[68px]">
            <section
              className="relative overflow-hidden rounded-2xl px-4 py-6"
              style={{ background: C.innerBg, border: `1px solid ${C.line}` }}
            >
              <div className="flex items-baseline justify-center gap-1">
                <span className="font-mono text-4xl font-semibold" style={{ color: C.gold }}>
                  {fmtNum(soldQty, 0)}
                </span>
                <span className="text-base font-semibold" style={{ color: C.gold }}>
                  {tokenDisplayLabel('uo')}
                </span>
              </div>
              <div className="mt-2 text-center text-sm" style={{ color: C.text }}>
                {qtyLabel}
              </div>
              {listTime ? (
                <div className="mt-6 text-right text-[11px]" style={{ color: C.muted }}>
                  {listTime}
                </div>
              ) : null}
            </section>

            <section>
              <div className="mb-3 px-1 text-sm font-semibold" style={{ color: C.text }}>
                {listTitle}
              </div>
              <div
                className="mb-2 grid grid-cols-[24px_1fr_52px_56px_72px] gap-1 px-1 text-[10px]"
                style={{ color: C.muted }}
              >
                <span>#</span>
                <span>{counterpartyLabel}</span>
                <span className="text-center">数量</span>
                <span className="text-center">成交价</span>
                <span className="text-right">时间</span>
              </div>
              {showBuyerSkeleton ? (
                <div className="space-y-3">
                  <LoadingSkeleton className="h-16" />
                  <LoadingSkeleton className="h-16" />
                </div>
              ) : !detail || detail.buyers.length === 0 ? (
                <div
                  className="rounded-2xl px-4 py-8 text-center text-xs"
                  style={{ background: C.innerBg, border: `1px solid ${C.line}`, color: C.muted }}
                >
                  {emptyListHint}
                </div>
              ) : (
                <div className="space-y-3">
                  {detail.buyers.map((row) => (
                    <div
                      key={`${row.index}-${row.deal_time}-${row.quantity_uo}`}
                      className="rounded-2xl px-3 py-3"
                      style={{ background: C.innerBg, border: `1px solid ${C.line}` }}
                    >
                      <div className="grid grid-cols-[24px_1fr_52px_56px_72px] items-center gap-1">
                        <span className="font-mono text-xs" style={{ color: C.gold }}>
                          {row.index}
                        </span>
                        <div className="flex min-w-0 items-center gap-2">
                          <ThemedAvatar src={row.avatar} seed={row.buyer_id} className="h-7 w-7 shrink-0" />
                          <span
                            className="truncate font-mono text-xs font-semibold"
                            style={{ color: C.gold }}
                          >
                            {row.buyer_id}
                          </span>
                        </div>
                        <span className="text-center font-mono text-xs" style={{ color: C.gold }}>
                          {fmtNum(row.quantity_uo, 0)}
                        </span>
                        <span className="text-center font-mono text-xs" style={{ color: C.gold }}>
                          {fmtNum(row.unit_price, 4)}
                        </span>
                        <span className="text-right text-[10px] leading-tight" style={{ color: C.muted }}>
                          {fmtLegacyTime(row.deal_time)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
